import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import eventBus from "../../utils/eventBus";
import SkitAdCard from "../../components/skitAd/skitAdCard";
import Remote from "../../components/remote/remote";
import {
  apiFetchSkitsForSkitsListingPage,
  apiDeleteSkitIdFromSourceIdAction,
} from "../../api/backend/skits";
import {
  apiRecordUserSkitNotInterested,
  apiFetchUserSkitNotInterested,
} from "../../api/backend/user";
import ClapperBoard from "../../components/clapperBoard/animatedClapperBoard";
import SmallPlainInsetBtn from "../../components/buttons/SmallPlainInsetBtn"; // Ensure this is the correct import path
import { MegaphoneIcon } from "../../components/icons";
import UniversalSearchLauncher from "../../components/universalSearchBar/universalSearchLauncher";
import Loader from "../../components/loader/loader";
import ReportModal from "../../components/reportModal/reportModal";
import { apiReportSkit } from "../../api/backend/skits";
import "./skitGrid.scss";

const Skits = () => {
  const {
    currentMemberId,
    handleLogout,
    openSkitUploadForm,
    notInterested,
    setNotInterested,
    setSuccessFlashMessage,
  } = useAuth();
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(null);
  const [isClapping, setIsClapping] = useState(false);
  const [skitsData, setSkitsData] = useState([]);
  const [colorPaletteVisible, setColorPaletteVisible] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportingSkit, setReportingSkit] = useState(null);
  const initialOffset =
    parseInt(localStorage.getItem("skitsListingOffset")) || 0;
  const [offset, setOffset] = useState(initialOffset); // the number of items recieved
  const maxSkitsPerCategory = 6; // for each offset limit
  const onLogoutClick = () => {
    handleLogout();
  };

  console.log("Skits component rendered");
  // Scroll event logging
  useEffect(() => {
    const handleScroll = () => {
      console.log("Scroll event triggered");
      const scrollTop = window.scrollY;
      setSticky(scrollTop > 100);
    };

    console.log("Adding scroll event listener...");
    window.addEventListener("scroll", handleScroll);

    return () => {
      console.log("Removing scroll event listener...");
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Define the fetch function with useCallback
  const fetchCurrentUserNotInterestedActivityLogs = useCallback(async () => {
    console.log("Fetching 'not interested' logs...");
    setLoading(true);
    try {
      const response = await apiFetchUserSkitNotInterested({
        loggedInAccountHolderId: currentMemberId, // was sourceId
      });

      if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
        await handleLogout();
        return;
      }

      const count =
        typeof response?.data?.unreadNotificationCount === "number"
          ? response.data.unreadNotificationCount
          : typeof response?.unreadNotificationCount === "number"
            ? response.unreadNotificationCount
            : null;
      if (typeof count === "number") {
        notificationsCountFetchedFromMiddleware(count);
      }

      // tolerant parse
      const body = response?.data !== undefined ? response.data : response;
      console.log("Fetched 'not interested' logs:", body);
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      setNotInterested(list.map((log) => log.relatedId));
    } catch (error) {
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        return;
      }
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      console.error("Failed to fetch 'not interested' logs:", error);
      setError("Failed to load 'not interested' logs");
    } finally {
      setLoading(false);
    }
  }, [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]);

  // // Use useEffect to call the function on mount or when currentMemberId changes
  useEffect(() => {
    console.log("notInterested updated", notInterested);
  }, [notInterested]);

  const handleNotInterested = useCallback(
    async (skit) => {
      console.log("Marking skit as 'not interested':", skit);
      try {
        const response = await apiRecordUserSkitNotInterested({
          loggedInAccountHolderId: currentMemberId,
          activity: "notInterested",
          relatedId: skit.id,
          targetId: skit.sourceId,
        });
        console.log("Marked as 'not interested' response:", response);
        // suspension eject
        if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge if backend included a count
        const count = response?.data?.unreadNotificationCount;
        if (typeof count === "number") {
          notificationsCountFetchedFromMiddleware(count);
        }
        if (response?.data && response.data.success) {
          setNotInterested((prev) => [...prev, skit.id]);
        }
      } catch (error) {
        // suspension on error path too
        if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge from error payload if present
        const errCount =
          error?.response?.data?.unreadNotificationCount ??
          error?.unreadNotificationCount ??
          null;
        if (typeof errCount === "number") {
          notificationsCountFetchedFromMiddleware(errCount);
        }

        console.error("Error marking skit as 'not interested':", error);
        setError("Failed to mark skit as 'not interested'");
      }
    },
    [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]
  );

  const fetchSkits = async (offset, categoryId = null) => {
    console.log("Fetching skits with offset and categoryId:", {
      offset,
      categoryId,
    });
    setLoading(true);
    try {
      const response = await apiFetchSkitsForSkitsListingPage(
        offset,
        maxSkitsPerCategory,
        categoryId
      );
      console.log("Fetched skits response:", response);
      if (response.status === 200 && response.data.success) {
        setSkitsData((prevSkits) =>
          offset === 0
            ? response.data.data
            : [...prevSkits, ...response.data.data]
        );
      }
    } catch (error) {
      console.error("Error fetching skits:", error);
      setError("Failed to fetch skits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUserNotInterestedActivityLogs();
  }, [fetchCurrentUserNotInterestedActivityLogs]);

  // A duplicate key warning error was likely happening because the afterUploadCloseModalRerender
  // function is fetching skits without resetting the offset, which may result in adding the
  // newly uploaded skit to the existing list without clearing the previous skits. This fixes that issue
  useEffect(() => {
    if (location.state?.refreshFromUpload) {
      setOffset(0);
      setSkitsData([]);
      fetchSkits(0, activeCategory);
      navigate(".", { replace: true, state: null }); // clear flag
    }
  }, [location.state, activeCategory, navigate]);

  const handleDeleteSourceIdRelatedIdSkit = (skitId) => {
    console.log("Deleting skit with ID:", skitId);
    apiDeleteSkitIdFromSourceIdAction({ skitId })
      .then((response) => {
        console.log("Delete response:", response);
        if (response.status === 200) {
          const updatedSkitsData = skitsData.filter(
            (skit) => skit.id !== skitId
          );
          setSkitsData(updatedSkitsData);
        } else {
          alert("Failed to delete the skit");
        }
      })
      .catch((error) => {
        console.error("Error deleting skit:", error);
        alert("An error occurred while deleting the skit");
      });
  };

  const toggleColorPaletteVisibility = () => {
    setColorPaletteVisible(!colorPaletteVisible);
  };

  useEffect(() => {
    fetchSkits(offset, activeCategory);
  }, [offset, activeCategory]);
  const handleViewMore = async () => {
    setIsClapping(true);
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate delay for animation
      const newOffset = offset + maxSkitsPerCategory;
      setOffset(newOffset);
    } catch (error) {
      console.error("Failed to load more skits:", error);
    } finally {
      setIsClapping(false);
      setLoading(false);
    }
  };

  const updateSkitsData = useCallback(async () => {
    setLoading(true);
    try {
      const updatedSkits = await apiFetchSkitsForSkitsListingPage(
        0,
        maxSkitsPerCategory,
        activeCategory
      );
      if (updatedSkits.status === 200 && updatedSkits.data.success) {
        setSkitsData(updatedSkits.data.data);
      }
    } catch (error) {
      console.error("Error updating skits:", error);
    } finally {
      setLoading(false);
    }
  }, [maxSkitsPerCategory, activeCategory]);

  useEffect(() => {
    const handleSkitPromotion = (promotedSkit) => {
      console.log("Skit promoted event triggered:", promotedSkit);
      setSkitsData((prevSkits) => {
        const updatedSkits = [
          promotedSkit,
          ...prevSkits.filter((skit) => skit.id !== promotedSkit.id),
        ];
        return updatedSkits;
      });
    };

    console.log("Adding skitPromoted listener...");
    eventBus.on("skitPromoted", handleSkitPromotion);

    return () => {
      console.log("Removing skitPromoted listener...");
      eventBus.off("skitPromoted", handleSkitPromotion);
    };
  }, []);

  const reportSkit = async (choices) => {
    // tiny inline flash so you can keep it local
    const flash = (msg, ms = 1800) => {
      setSuccessFlashMessage?.(msg);
      clearTimeout(reportSkit._t);
      reportSkit._t = setTimeout(() => setSuccessFlashMessage?.(null), ms);
    };
    try {
      const res = await apiReportSkit({
        loggedInAccountHolderId: currentMemberId,   // ← keep nomenclature
        skitId: reportingSkit.id,
        reason: JSON.stringify(choices),
      });
      // eject if backend signals suspension (auth/domain)
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread count (body preferred, then header)
      const bodyCount = Number(res?.data?.unreadNotificationCount ?? res?.unreadNotificationCount);
      if (Number.isFinite(bodyCount)) notificationsCountFetchedFromMiddleware?.(bodyCount);
      const headerCount = Number(res?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount)) notificationsCountFetchedFromMiddleware?.(headerCount);
      flash("Report sent ✅");
      // if backend suspended it, re-fetch "page 1" of live skits
      const skitStatus = res?.data?.data?.skitStatus ?? res?.data?.skitStatus;
      if (skitStatus === "suspended") {
        fetchSkits(0, activeCategory); // <-- offset 0, existing category filter
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware?.(errCount);
      }
      flash("Could not submit report. Try again.", 2200);
    } finally {
      setReportingSkit(null);
    }
  };
  

  return (
    <div id="skitsListingPage">
      <div
        className={`${colorPaletteVisible ? "glassHolder" : "searchRemoteWrapper"} ${
          sticky ? "sticky" : "searchRemoteWrapper"
        }`}
      >
        <Remote
          onStartSkitUploadFlowClick={(statePayload) =>
            navigate("/upload-skit", { state: statePayload })
          }
          toggleColorPaletteVisibility={toggleColorPaletteVisibility}
          colorPaletteVisible={colorPaletteVisible}
          setColorPaletteVisible={setColorPaletteVisible}
          onChannelChange={(categoryId) => {
            setActiveCategory(categoryId);
            setOffset(0);
            setSkitsData([]);
            fetchSkits(0, categoryId);
          }}
          onClick={onLogoutClick}
        />
        <UniversalSearchLauncher />
      </div>
      <div className="skits-grid">
        {skitsData
          .filter((skit) => !notInterested.includes(skit.id)) // Filter out not interested skits
          .map((skit) => (
            <SkitAdCard
              key={skit.id}
              specificSkitData={skit}
              currentMemberId={currentMemberId}
              onDelete={() => handleDeleteSourceIdRelatedIdSkit(skit.id)} // Passing skit.id to the delete handler
              onNotInterested={() => handleNotInterested(skit)}
              updateSkitsData={updateSkitsData}
              isUploadVideoFormOpen={openSkitUploadForm} // Pass the prop here to manage z-index in Skit
              onBlowWhistle={(skitToReport) => setReportingSkit(skitToReport)}
            />
          ))}
      </div>
      {skitsData.length > 0 && (
        <div className="View-More-Action">
          {loading && <Loader className="View-More-Action" />}
          <ClapperBoard isClapping={isClapping} />
          <SmallPlainInsetBtn
            image={
              <MegaphoneIcon
                height="20px"
                width="20px"
                style={{ marginRight: "3px" }}
              />
            }
            text="View More Action"
            width="188px"
            style={{
              fontSize: "1.25rem",
            }}
            onClick={handleViewMore}
          />
        </div>
      )}
      {reportingSkit && (
        <ReportModal
          kind="skit"
          onBlowWhistle={reportSkit}
          onCancel={() => setReportingSkit(null)}
        />
      )}
    </div>
  );
};
export default Skits;
